/**
 * Enochian Watchman Widget
 * True Biblical Schedule for the Nation of Yisrael
 * 364-Day Solar Cycle (Enoch 72-74) & Continuous Weekly Sabbath (Genesis 2:2-3)
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Gate colors for visual mapping
const GATE_COLORS = {
  1: { bg: "#1a1a3a", border: "#4169E1", name: "Royal Blue" },
  2: { bg: "#1a2a3a", border: "#20B2AA", name: "Light Sea Green" },
  3: { bg: "#1a3a2a", border: "#32CD32", name: "Lime Green" },
  4: { bg: "#3a3a1a", border: "#FFD700", name: "Gold" },
  5: { bg: "#3a2a1a", border: "#FFA500", name: "Orange" },
  6: { bg: "#3a1a1a", border: "#FF6347", name: "Tomato" },
};

// Continuous Weekly Sabbath (Genesis 2:2-3) — the seventh-day rest
// established at Creation, kept unbroken week after week.
// Legacy day-of-month anchors — retained only as an overlay reference
// for the biblical-month chart. The AUTHORITATIVE weekly Sabbath is
// the continuous Genesis 2:2-3 seventh-day rest (Friday sundown →
// Saturday sundown), surfaced by the Gregorian Sabbath tiles below.
const SABBATH_DAYS = [7, 14, 21, 28];

function EnochianWatchman({ compact = false, onHighSabbath = null }) {
  const [celestialData, setCelestialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // GEOGRAPHIC ANCHOR: North American Continent - Mississippi River Valley (St. Louis/Cahokia)
  // Fallback coordinates: ~38.6270° N, 90.1994° W (St. Louis, MO - center of the Promised Land)
  const [location, setLocation] = useState({ lat: 38.6270, lon: -90.1994 });
  const [locationName, setLocationName] = useState("St. Louis/Cahokia (Default)");
  const [expanded, setExpanded] = useState(!compact);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [activeView, setActiveView] = useState("calendar"); // "calendar" or "celestial"
  const [solarGateData, setSolarGateData] = useState(null); // Dynamic calendar state

  // Get user's geolocation - ALWAYS try to use device location
  const getUserLocation = useCallback(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
          setLocationName("Your Location");
        },
        (err) => {
          console.warn("Geolocation error:", err);
          // Keep default St. Louis/Cahokia location
          setLocationName("St. Louis/Cahokia (Default)");
        },
        {
          timeout: 10000,
          enableHighAccuracy: true,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    }
  }, []);

  // Fetch celestial data
  const fetchCelestialData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      // Get user's timezone offset in hours for accurate sundown detection
      const tzOffsetMinutes = new Date().getTimezoneOffset();
      const tzOffsetHours = -tzOffsetMinutes / 60;
      
      // Send user's local date AND time for accurate biblical day calculation
      // Biblical days change at SUNDOWN, so we need the full local datetime
      const now = new Date();
      const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const localHour = now.getHours();
      const localMinute = now.getMinutes();
      
      const response = await axios.get(`${API}/celestial/data`, {
        params: { 
          latitude: location.lat, 
          longitude: location.lon,
          tz_offset_hours: tzOffsetHours,
          local_date: localDate,
          local_hour: localHour,
          local_minute: localMinute
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCelestialData(response.data);
      setError(null);

      // Trigger callbacks for High Sabbath curriculum lock
      if (response.data.notifications?.is_sabbath && onHighSabbath) {
        onHighSabbath(true);
      }

      // Check for Sabbath/Preparation notifications
      if (response.data.notifications?.sabbath_notification) {
        showSabbathAlert(response.data.notifications);
      }
      
      // Check for new month
      if (response.data.notifications?.new_month_alert) {
        showNewMonthAlert();
      }
    } catch (err) {
      console.error("Failed to fetch celestial data:", err);
      setError("Unable to fetch astronomical data");
    } finally {
      setLoading(false);
    }
  }, [location, onHighSabbath]);

  // Initial load and periodic refresh
  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  useEffect(() => {
    if (location.lat && location.lon) {
      fetchCelestialData();
      // Refresh every 5 minutes
      const interval = setInterval(fetchCelestialData, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [location, fetchCelestialData]);

  // Show Sabbath alert
  const showSabbathAlert = (notifications) => {
    if ("Notification" in window && Notification.permission === "granted") {
      if (notifications.is_sabbath) {
        new Notification("Sabbath Alert - Nation of Yisrael", {
          body: notifications.sabbath_notification,
          icon: "/logo192.png",
          tag: "sabbath-alert",
        });
      } else if (notifications.is_preparation) {
        new Notification("Preparation Day - Nation of Yisrael", {
          body: notifications.sabbath_notification,
          icon: "/logo192.png",
          tag: "preparation-alert",
        });
      }
    }
  };

  // Show New Month alert
  const showNewMonthAlert = () => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("New Month - Nation of Yisrael", {
        body: "The New Month has begun! Reset your Sabbath count to Day 1.",
        icon: "/logo192.png",
        tag: "new-month",
      });
    }
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        setShowNotificationPrompt(false);
        // Register for push notifications
        try {
          const token = localStorage.getItem("token");
          await axios.post(
            `${API}/celestial/subscribe-notifications`,
            ["sabbath", "new_month", "moadim", "high_sabbath"],
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          console.error("Failed to subscribe:", err);
        }
      }
    }
  };

  // Get moon emoji based on phase
  const getMoonEmoji = (illumination, isWaxing) => {
    if (illumination < 5) return "🌑";
    if (illumination < 25) return isWaxing ? "🌒" : "🌘";
    if (illumination < 50) return isWaxing ? "🌓" : "🌗";
    if (illumination < 75) return isWaxing ? "🌔" : "🌖";
    return "🌕";
  };

  // Fetch Solar Gate Tracker data
  useEffect(() => {
    const fetchSolarGate = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/celestial/solar-gate-tracker`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSolarGateData(response.data.solar_gate_tracker);
      } catch (err) {
        console.log("Solar Gate Tracker not available yet");
      }
    };
    if (celestialData) {
      fetchSolarGate();
    }
  }, [celestialData]); // Re-fetch when celestial data updates

  // Render loading state
  if (loading) {
    return (
      <div
        className="card"
        style={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
          border: "2px solid #FFD700",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div style={{ color: "#FFD700", fontSize: "1.5rem" }}>🌙</div>
        <p style={{ color: "#aaa" }}>Loading Celestial Data...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div
        className="card"
        style={{
          background: "#2a1a1a",
          border: "2px solid #ff6b6b",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <p style={{ color: "#ff6b6b" }}>{error}</p>
        <button
          onClick={fetchCelestialData}
          style={{ marginTop: "10px", padding: "8px 16px" }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!celestialData) return null;

  const { sun, moon, enochian, notifications, biblical } = celestialData;
  const gateStyle = GATE_COLORS[enochian.current_gate];
  
  // DYNAMIC BIBLICAL DATE CALCULATION (NO STATIC ANCHOR)
  const calculateClientBiblicalDate = () => {
    const now = new Date();
    const localHour = now.getHours();
    const isAfterSundown = localHour >= 19; // Sunset approx 7 PM
    
    // CHECK SOLAR GATE DATA - If calendar not active, we're in JUBILEE LAPSE
    if (!solarGateData?.calendar_state?.active) {
      return {
        day: 0,
        month: 0,
        month_name: "JUBILEE GATE",
        is_after_sunset: isAfterSundown,
        in_jubilee_lapse: true,
        status_message: solarGateData?.status?.message || "Waiting for Solar Alignment"
      };
    }
    
    // DYNAMIC ANCHOR - Use stored Abib 1 from Solar Gate Tracker
    const abib1_start = solarGateData?.calendar_state?.abib_1_start;
    if (!abib1_start) {
      return {
        day: 0,
        month: 0,
        month_name: "JUBILEE GATE",
        is_after_sunset: isAfterSundown,
        in_jubilee_lapse: true,
        status_message: "Waiting for Luminary Gate Trigger"
      };
    }
    
    const abib1Date = new Date(abib1_start);
    
    // Get user's local date at midnight
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Days since dynamic Abib 1
    const msPerDay = 24 * 60 * 60 * 1000;
    let daysSinceAbib = Math.floor((today - abib1Date) / msPerDay);
    
    // SUNDOWN LOGIC: Before sundown, we're in the previous biblical day
    if (!isAfterSundown) {
      daysSinceAbib -= 1;
    }
    
    // Ensure non-negative
    if (daysSinceAbib < 0) daysSinceAbib = 0;
    
    // Calculate month and day (364-day year with quarters of 91 days)
    // Months 3, 6, 9, 12 have 31 days (intercalary)
    const Q1_END = 91;   // End of Month 3
    const Q2_END = 182;  // End of Month 6  
    const Q3_END = 273;  // End of Month 9
    
    let month, day;
    
    if (daysSinceAbib < Q1_END) {
      month = Math.floor(daysSinceAbib / 30) + 1;
      day = (daysSinceAbib % 30) + 1;
      if (month === 3 && day > 30) day = 31;
    } else if (daysSinceAbib < Q2_END) {
      const adj = daysSinceAbib - Q1_END;
      month = Math.floor(adj / 30) + 4;
      day = (adj % 30) + 1;
      if (month === 6 && day > 30) day = 31;
    } else if (daysSinceAbib < Q3_END) {
      const adj = daysSinceAbib - Q2_END;
      month = Math.floor(adj / 30) + 7;
      day = (adj % 30) + 1;
      if (month === 9 && day > 30) day = 31;
    } else {
      const adj = daysSinceAbib - Q3_END;
      month = Math.floor(adj / 30) + 10;
      day = (adj % 30) + 1;
      if (month === 12 && day > 30) day = 31;
    }
    
    // Month names
    const MONTH_NAMES = [
      "", "Abib (Aviv)", "Ziv", "Sivan", "Tammuz", "Av", "Elul",
      "Ethanim (Tishrei)", "Bul", "Kislev", "Tevet", "Shevat", "Adar"
    ];
    
    return {
      day: day,
      month: month,
      month_name: MONTH_NAMES[month] || "Unknown",
      is_after_sunset: isAfterSundown,
      in_jubilee_lapse: false,
      day_of_year: daysSinceAbib + 1
    };
  };
  
  // USE SERVER DATA AS SOURCE OF TRUTH
  // The backend's astronomy_service.py now correctly calculates the biblical date
  // using Portal Scaling (Enochian 364-day cycle) and Sun Elevation triggers
  const serverBiblicalDate = biblical?.date || { day: 0, month: 0, month_name: "Loading..." };
  
  // Only fall back to client calculation if server data is not available
  const clientBiblicalDate = calculateClientBiblicalDate();
  const biblicalDate = serverBiblicalDate.day > 0 ? serverBiblicalDate : clientBiblicalDate;
  
  const sabbathStatus = biblical?.sabbath || { is_sabbath: false, is_preparation: false };
  const lunarLag = biblical?.lunar_lag || { needs_recalibration: false, accumulated_drift: 0 };
  
  // Calculate next study day info
  const getNextStudyDayInfo = () => {
    const currentDay = biblicalDate.day;
    if (sabbathStatus.is_sabbath) {
      // On Sabbath, next study day is tomorrow
      return {
        show: true,
        message: "Curriculum resumes tomorrow",
        daysUntil: 1,
        type: "sabbath"
      };
    } else if (sabbathStatus.is_preparation) {
      // On Preparation Day, warn about upcoming Sabbath
      return {
        show: true,
        message: `Tomorrow is Sabbath (Day ${currentDay + 1}). Prepare today!`,
        daysUntil: 0,
        type: "preparation"
      };
    }
    return { show: false };
  };
  
  const nextStudyInfo = getNextStudyDayInfo();

  // Compact widget view
  if (compact && !expanded) {
    // JUBILEE LAPSE STATUS - When calendar not active
    if (biblicalDate.in_jubilee_lapse) {
      return (
        <div
          data-testid="enochian-watchman-lapse"
          onClick={() => setExpanded(true)}
          style={{
            background: "linear-gradient(135deg, #2a1a0a 0%, #1a0a0a 100%)",
            border: "2px solid #8B4513",
            borderRadius: "12px",
            padding: "12px 16px",
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "1.5rem" }}>🔒</span>
            <div>
              <div style={{ color: "#FFD700", fontSize: "0.9rem", fontWeight: "bold" }}>
                JUBILEE GATE
              </div>
              <div style={{ color: "#aaa", fontSize: "0.75rem" }}>
                {biblicalDate.status_message || "Waiting for Solar Alignment"}
              </div>
              <div style={{ color: "#8B4513", fontSize: "0.7rem", marginTop: "2px" }}>
                ☀️ Sun: {solarGateData?.sun?.longitude?.toFixed(1)}° | Gate {solarGateData?.sun?.current_gate}
                {enochian?.gate_direction && (
                  <span
                    data-testid="watchman-compact-direction"
                    style={{
                      marginLeft: 6,
                      color: enochian.gate_direction === "Ascending" ? "#7CFC7C" : "#FFB27C",
                      fontWeight: 700,
                    }}
                  >
                    {enochian.gate_direction === "Ascending" ? "↑" : "↓"} {enochian.gate_direction}
                  </span>
                )}
              </div>
            </div>
          </div>
          <span style={{ color: "#8B4513" }}>▼</span>
        </div>
      );
    }
    
    return (
      <div
        data-testid="enochian-watchman-compact"
        onClick={() => setExpanded(true)}
        style={{
          background: sabbathStatus.is_sabbath 
            ? "linear-gradient(135deg, #8B4513 0%, #CD853F 100%)"
            : sabbathStatus.is_preparation
            ? "linear-gradient(135deg, #4a3a2a 0%, #3a2a1a 100%)"
            : gateStyle.bg,
          border: `2px solid ${sabbathStatus.is_sabbath ? "#FFD700" : sabbathStatus.is_preparation ? "#FFA500" : gateStyle.border}`,
          borderRadius: "12px",
          padding: "12px 16px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "1.5rem" }}>
            {sabbathStatus.is_sabbath ? "🕊️" : sabbathStatus.is_preparation ? "⚙️" : getMoonEmoji(moon.illumination, moon.is_waxing)}
          </span>
          <div>
            <div style={{ color: "#FFD700", fontSize: "0.9rem", fontWeight: "bold" }}>
              Day {biblicalDate.day} of {biblicalDate.month_name}
            </div>
            <div style={{ color: "#ccc", fontSize: "0.75rem" }}>
              {sabbathStatus.is_sabbath ? "SABBATH - Set Apart" 
                : sabbathStatus.is_preparation ? `Prep Day - Sabbath Tomorrow`
                : `${sabbathStatus.days_to_sabbath || 0} days to Sabbath`}
            </div>
            {nextStudyInfo.show && nextStudyInfo.type === "sabbath" && (
              <div style={{ color: "#90EE90", fontSize: "0.7rem", marginTop: "2px" }}>
                📚 Curriculum resumes tomorrow
              </div>
            )}
          </div>
        </div>
        <span style={{ color: gateStyle.border }}>▼</span>
      </div>
    );
  }

  // Full widget view
  return (
    <div
      data-testid="enochian-watchman-widget"
      className="card"
      style={{
        background: `linear-gradient(135deg, ${sabbathStatus.is_sabbath ? "#2a1a0a" : gateStyle.bg} 0%, #0a0a0a 100%)`,
        border: `2px solid ${gateStyle.border}`,
        padding: 0,
        overflow: "hidden",
      }}
    >
      {/* Header with Greeting */}
      <div
        style={{
          background: sabbathStatus.is_sabbath 
            ? "linear-gradient(90deg, #8B4513 0%, #CD853F 100%)"
            : `linear-gradient(90deg, ${gateStyle.border}20 0%, transparent 100%)`,
          padding: "15px 20px",
          borderBottom: `1px solid ${sabbathStatus.is_sabbath ? "#FFD700" : gateStyle.border}40`,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ color: "#FFD700", margin: 0, fontSize: "1.1rem", fontFamily: "Georgia, serif" }}>
              📜 Enochian Watchman
            </h3>
            <p style={{ color: "#ccc", margin: "3px 0 0 0", fontSize: "0.75rem" }}>
              364-Day Solar Cycle • Jubilee Edition
            </p>
          </div>
          
          {/* DUAL-CLOCK: Civil Date (for state compliance) */}
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "#666", fontSize: "0.65rem", textTransform: "uppercase" }}>
              Civil Date
            </div>
            <div style={{ color: "#888", fontSize: "0.75rem" }}>
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            {/* GPS Location Indicator */}
            <div style={{ 
              color: locationName === "Your Location" ? "#90EE90" : "#888", 
              fontSize: "0.6rem", 
              marginTop: "2px" 
            }}>
              📍 {locationName}
            </div>
          </div>
          {compact && (
            <button
              onClick={() => setExpanded(false)}
              style={{
                background: "transparent",
                border: "none",
                color: gateStyle.border,
                cursor: "pointer",
                fontSize: "1.2rem",
              }}
            >
              ▲
            </button>
          )}
        </div>
        
        {/* Greeting for Nation of Yisrael - Using CLIENT-SIDE biblical date */}
        <p style={{ color: "#FFD700", margin: "10px 0 0", fontSize: "0.85rem", fontStyle: "italic" }}>
          Shalom, Nation of Yisrael! Today is the {biblicalDate.day}{biblicalDate.day === 1 ? 'st' : biblicalDate.day === 2 ? 'nd' : biblicalDate.day === 3 ? 'rd' : 'th'} day of {biblicalDate.month_name}.
        </p>
      </div>

      {/* Sabbath/Preparation Alert Banner */}
      {notifications?.sabbath_notification && (
        <div
          style={{
            background: sabbathStatus.is_sabbath 
              ? "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)"
              : "linear-gradient(135deg, #4a3a2a 0%, #3a2a1a 100%)",
            padding: "12px 20px",
            borderBottom: `2px solid ${sabbathStatus.is_sabbath ? "#FFD700" : "#CD853F"}`,
          }}
        >
          <div style={{ color: "#FFD700", fontWeight: "bold", fontSize: "0.85rem" }}>
            {sabbathStatus.is_sabbath ? "🕊️ SABBATH - SET APART" : "⚙️ PREPARATION DAY"}
          </div>
          <div style={{ color: "#fff", fontSize: "0.8rem", marginTop: "3px" }}>
            {notifications.sabbath_notification}
          </div>
          {sabbathStatus.sabbath_name && (
            <div style={{ color: "#FFD700", fontSize: "0.75rem", marginTop: "3px" }}>
              {sabbathStatus.sabbath_name}
            </div>
          )}
        </div>
      )}

      {/* View Toggle */}
      <div style={{ display: "flex", borderBottom: "1px solid #333", background: "#111" }}>
        <button
          onClick={() => setActiveView("calendar")}
          style={{
            flex: 1,
            padding: "8px",
            background: activeView === "calendar" ? "#2a1a0a" : "transparent",
            border: "none",
            borderBottom: activeView === "calendar" ? "2px solid #FFD700" : "none",
            color: activeView === "calendar" ? "#FFD700" : "#888",
            cursor: "pointer",
            fontSize: "0.8rem",
          }}
        >
          📜 Biblical
        </button>
        <button
          onClick={() => setActiveView("celestial")}
          style={{
            flex: 1,
            padding: "8px",
            background: activeView === "celestial" ? "#1a1a3a" : "transparent",
            border: "none",
            borderBottom: activeView === "celestial" ? "2px solid #87CEEB" : "none",
            color: activeView === "celestial" ? "#87CEEB" : "#888",
            cursor: "pointer",
            fontSize: "0.8rem",
          }}
        >
          🌙 Celestial
        </button>
      </div>

      {/* Main Content */}
      <div style={{ padding: "20px" }}>
        {/* Biblical Calendar View */}
        {activeView === "calendar" && (
          <>
            {/* Biblical Date Display */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
                marginBottom: "15px",
              }}
            >
              <div
                style={{
                  background: "#111",
                  borderRadius: "10px",
                  padding: "15px",
                  textAlign: "center",
                  border: "1px solid #CD853F40",
                }}
              >
                <div style={{ color: "#FFD700", fontSize: "2.5rem", fontWeight: "bold" }}>
                  {biblicalDate.day}
                </div>
                <div style={{ color: "#ccc", fontSize: "0.8rem" }}>Day of Month</div>
                {/* Role label — surfaces today's role under the big day number
                    so the user immediately sees "PREPARATION DAY" / "SABBATH" /
                    "WORSHIP" without having to interpret the gate pill. */}
                {sabbathStatus.day_role && (
                  <div
                    data-testid="watchman-day-role-label"
                    style={{
                      marginTop: "4px",
                      color:
                        sabbathStatus.day_role === "sabbath"
                          ? "#FFD700"
                          : sabbathStatus.day_role === "worship"
                          ? "#FFD700"
                          : sabbathStatus.day_role === "intercalary"
                          ? "#FFD700"
                          : "#CD853F",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    {sabbathStatus.is_preparation
                      ? "Preparation Day"
                      : sabbathStatus.day_role === "sabbath"
                      ? "Sabbath"
                      : sabbathStatus.day_role === "worship"
                      ? "Worship Day"
                      : sabbathStatus.day_role === "intercalary"
                      ? "Intercalary Portal"
                      : "Work Day"}
                    <div style={{ fontSize: "0.65rem", opacity: 0.75, fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>
                      Gate is {sabbathStatus.gate_status === "SHUT" ? "CLOSED" : "OPEN"}
                    </div>
                  </div>
                )}
                {/* Ezekiel 46:1 Gate Pill — OPEN on worship/sabbath/intercalary, SHUT on work days */}
                {sabbathStatus.gate_status && (
                  <div
                    data-testid="watchman-gate-pill"
                    title={sabbathStatus.gate_reason || ""}
                    style={{
                      marginTop: "10px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "4px 10px",
                      borderRadius: "999px",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        sabbathStatus.gate_status === "OPEN"
                          ? "rgba(255, 215, 0, 0.15)"
                          : "rgba(205, 133, 63, 0.10)",
                      color: sabbathStatus.gate_status === "OPEN" ? "#FFD700" : "#CD853F",
                      border:
                        sabbathStatus.gate_status === "OPEN"
                          ? "1px solid #FFD70080"
                          : "1px solid #CD853F60",
                    }}
                  >
                    <span style={{ fontSize: "0.85rem", lineHeight: 1 }}>
                      {sabbathStatus.gate_status === "OPEN" ? "⛩" : "▮"}
                    </span>
                    Gate {sabbathStatus.gate_status === "SHUT" ? "CLOSED" : sabbathStatus.gate_status}
                    {sabbathStatus.day_role && (
                      <span style={{ opacity: 0.75, fontWeight: 500 }}>
                        · {sabbathStatus.day_role === "sabbath"
                          ? "Sabbath"
                          : sabbathStatus.day_role === "worship"
                          ? "Worship"
                          : sabbathStatus.day_role === "intercalary"
                          ? "Portal"
                          : sabbathStatus.work_day_number
                          ? `Work ${sabbathStatus.work_day_number}/6`
                          : "Work"}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div
                style={{
                  background: "#111",
                  borderRadius: "10px",
                  padding: "15px",
                  textAlign: "center",
                  border: "1px solid #CD853F40",
                }}
              >
                <div style={{ color: "#CD853F", fontSize: "1rem", fontWeight: "bold" }}>
                  {biblicalDate.month_name}
                </div>
                <div style={{ color: "#ccc", fontSize: "0.8rem", marginTop: "5px" }}>
                  Month {biblicalDate.month}
                </div>
              </div>
            </div>

            {/* Gregorian Weekly Sabbath — continuous seventh-day rest
                (Genesis 2:2-3). Replaces the prior day-of-month
                cadence entirely. Each tile shows the Gregorian day
                name with its numbered day-of-the-week underneath
                (Sun=1 … Sat=7, biblical week ordering). Today is
                gold-highlighted; Saturday is the Sabbath column;
                Friday is Preparation. */}
            <div style={{ marginBottom: "15px" }}>
              <div style={{ color: "#888", fontSize: "0.75rem", marginBottom: "8px" }}>
                GREGORIAN WEEKLY SABBATH (Fri sundown → Sat sundown)
              </div>
              <div style={{ display: "flex", gap: "6px" }}>
                {[
                  { name: "Sun", num: 1, bw: 6 },
                  { name: "Mon", num: 2, bw: 0 },
                  { name: "Tue", num: 3, bw: 1 },
                  { name: "Wed", num: 4, bw: 2 },
                  { name: "Thu", num: 5, bw: 3 },
                  { name: "Fri", num: 6, bw: 4 },
                  { name: "Sat", num: 7, bw: 5 },
                ].map(({ name, num, bw }) => {
                  const todayBw = sabbathStatus?.gregorian_weekday;
                  const isToday = todayBw === bw;
                  const isSabbathCol = bw === 5;
                  const isPrepCol = bw === 4;
                  return (
                    <div
                      key={name}
                      data-testid={`watchman-greg-day-${name.toLowerCase()}`}
                      style={{
                        flex: 1,
                        padding: "10px 5px",
                        borderRadius: "8px",
                        background: isToday
                          ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                          : isSabbathCol
                          ? "#2a1a1a"
                          : "#1a1a1a",
                        border: `2px solid ${
                          isToday
                            ? "#FFD700"
                            : isSabbathCol
                            ? "#CD853F"
                            : isPrepCol
                            ? "#555"
                            : "#333"
                        }`,
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          color: isToday ? "#000" : "#CD853F",
                          fontWeight: "bold",
                          fontSize: "0.95rem",
                        }}
                      >
                        {name}
                      </div>
                      <div
                        style={{
                          color: isToday ? "#000" : "#FFD700",
                          fontSize: "1.05rem",
                          fontWeight: "bold",
                          marginTop: "2px",
                        }}
                      >
                        {num}
                      </div>
                      <div
                        style={{
                          color: isToday ? "#333" : "#666",
                          fontSize: "0.55rem",
                          marginTop: "1px",
                        }}
                      >
                        {isSabbathCol ? "Sabbath" : isPrepCol ? "Prep" : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ color: "#666", fontSize: "0.65rem", marginTop: "5px", textAlign: "center" }}>
                Biblical Day {biblicalDate.day} of {biblicalDate.month_name} (tracked independently)
              </div>
            </div>

            {/* Upcoming Mo'adim */}
            {biblical?.moadim?.upcoming && biblical.moadim.upcoming.length > 0 && (
              <div
                style={{
                  background: "#111",
                  borderRadius: "10px",
                  padding: "12px",
                  border: "1px solid #CD853F40",
                }}
              >
                <div style={{ color: "#CD853F", fontWeight: "bold", fontSize: "0.8rem", marginBottom: "8px" }}>
                  📅 UPCOMING MO'ADIM
                </div>
                {biblical.moadim.upcoming.slice(0, 2).map((moed, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "5px 0",
                      borderBottom: idx === 0 ? "1px solid #333" : "none",
                    }}
                  >
                    <span style={{ color: "#FFD700", fontSize: "0.8rem" }}>{moed.name}</span>
                    <span style={{ color: moed.days_until <= 7 ? "#ff6b6b" : "#90EE90", fontSize: "0.8rem" }}>
                      {moed.days_until}d
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Next Study Day Alert */}
            {nextStudyInfo.show && (
              <div
                style={{
                  background: nextStudyInfo.type === "sabbath" 
                    ? "linear-gradient(135deg, #1a3a1a 0%, #0a2a0a 100%)"
                    : "linear-gradient(135deg, #3a3a1a 0%, #2a2a0a 100%)",
                  borderRadius: "10px",
                  padding: "12px",
                  marginTop: "10px",
                  border: nextStudyInfo.type === "sabbath" ? "1px solid #90EE90" : "1px solid #FFD700",
                }}
              >
                <div style={{ 
                  color: nextStudyInfo.type === "sabbath" ? "#90EE90" : "#FFD700", 
                  fontWeight: "bold", 
                  fontSize: "0.8rem", 
                  marginBottom: "5px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}>
                  {nextStudyInfo.type === "sabbath" ? "📚" : "⚙️"} NEXT STUDY DAY
                </div>
                <div style={{ color: "#ccc", fontSize: "0.8rem" }}>
                  {nextStudyInfo.message}
                </div>
                {nextStudyInfo.type === "preparation" && (
                  <div style={{ color: "#888", fontSize: "0.7rem", marginTop: "5px" }}>
                    Complete your lessons before sunset
                  </div>
                )}
              </div>
            )}

            {/* Lunar Lag Warning (Enoch 74) */}
            {lunarLag.accumulated_drift > 5 && (
              <div
                style={{
                  background: lunarLag.needs_recalibration 
                    ? "linear-gradient(135deg, #4d1a1a 0%, #2a0a0a 100%)"
                    : "linear-gradient(135deg, #2a2a3a 0%, #1a1a2a 100%)",
                  borderRadius: "10px",
                  padding: "12px",
                  marginTop: "10px",
                  border: lunarLag.needs_recalibration ? "1px solid #ff6b6b" : "1px solid #4169E1",
                }}
              >
                <div style={{ 
                  color: lunarLag.needs_recalibration ? "#ff6b6b" : "#87CEEB", 
                  fontWeight: "bold", 
                  fontSize: "0.8rem", 
                  marginBottom: "5px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}>
                  {lunarLag.needs_recalibration ? "⚠️" : "🌙"} LUNAR LAG (ENOCH 74)
                </div>
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  marginBottom: "5px"
                }}>
                  <span style={{ color: "#aaa", fontSize: "0.75rem" }}>Annual Drift:</span>
                  <span style={{ color: "#fff", fontSize: "0.8rem" }}>~{lunarLag.annual_drift_days} days</span>
                </div>
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  marginBottom: "5px"
                }}>
                  <span style={{ color: "#aaa", fontSize: "0.75rem" }}>Accumulated:</span>
                  <span style={{ 
                    color: lunarLag.accumulated_drift > 20 ? "#ff6b6b" : lunarLag.accumulated_drift > 10 ? "#FFD700" : "#90EE90", 
                    fontSize: "0.8rem",
                    fontWeight: "bold"
                  }}>
                    {lunarLag.accumulated_drift.toFixed(1)} days
                  </span>
                </div>
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center" 
                }}>
                  <span style={{ color: "#aaa", fontSize: "0.75rem" }}>Next Equinox:</span>
                  <span style={{ color: "#87CEEB", fontSize: "0.8rem" }}>{lunarLag.days_to_equinox}d</span>
                </div>
                {lunarLag.needs_recalibration && (
                  <div style={{ 
                    color: "#ff6b6b", 
                    fontSize: "0.7rem", 
                    marginTop: "8px",
                    padding: "6px",
                    background: "#2a0a0a",
                    borderRadius: "4px",
                    textAlign: "center"
                  }}>
                    ⚠️ HIGH DRIFT: Absorbed by Jubilee cycle (NOT 13th month)
                  </div>
                )}
                {!lunarLag.needs_recalibration && lunarLag.accumulated_drift > 15 && (
                  <div style={{ 
                    color: "#FFD700", 
                    fontSize: "0.7rem", 
                    marginTop: "8px",
                    textAlign: "center"
                  }}>
                    Drift absorbed by Jubilee cycle
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Celestial View (Original) */}
        {activeView === "celestial" && (
          <>
            {/* iter 68 — Solstice turn-back banner. Fires on the
                3-4 day window at Summer & Winter Solstices when
                |δ| ≥ 23.39°. Enoch 72:32-37 describes the sun
                "turning back" at both extremes — the banner keeps
                a student from missing the observance. */}
            {enochian?.solstice_turn_back?.active && (
              <div
                data-testid="watchman-solstice-turn-back"
                style={{
                  background: enochian.solstice_turn_back.type === "summer"
                    ? "linear-gradient(135deg, #3b2a0d 0%, #5a3a10 100%)"
                    : "linear-gradient(135deg, #0d1e3b 0%, #10305a 100%)",
                  border: `2px solid ${enochian.solstice_turn_back.type === "summer" ? "#FFD700" : "#87CEEB"}`,
                  borderRadius: 12,
                  padding: "14px 16px",
                  marginBottom: 15,
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <div style={{ fontSize: "1.8rem", lineHeight: 1 }}>
                  {enochian.solstice_turn_back.type === "summer" ? "☀️" : "❄️"}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    color: enochian.solstice_turn_back.type === "summer" ? "#FFD700" : "#87CEEB",
                    fontWeight: 800, letterSpacing: 0.4, fontSize: "0.85rem",
                    textTransform: "uppercase", marginBottom: 4,
                  }}>
                    {enochian.solstice_turn_back.type === "summer"
                      ? "Summer Solstice · Sun turns back"
                      : "Winter Solstice · Sun turns back"}
                  </div>
                  <div style={{ color: "#ddd", fontSize: "0.82rem", lineHeight: 1.5 }}>
                    {enochian.solstice_turn_back.message}
                  </div>
                </div>
              </div>
            )}

            {/* Gate & Moon Row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              {/* Sun Gate */}
              <div
                style={{
                  background: "#111",
                  borderRadius: "10px",
                  padding: "15px",
                  textAlign: "center",
                  border: `1px solid ${gateStyle.border}40`,
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "8px" }}>☀️</div>
                <div style={{ color: gateStyle.border, fontWeight: "bold", fontSize: "1.1rem" }}>
                  {enochian.gate_info.name}
                </div>
                <div style={{ color: "#aaa", fontSize: "0.8rem", marginTop: "5px" }} data-testid="watchman-gate-number">
                  Gate {enochian.current_gate} of 6
                </div>
                {enochian.gate_direction && (
                  <div
                    data-testid="watchman-gate-phase"
                    style={{
                      marginTop: 6,
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: 999,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      background: enochian.gate_direction === "Ascending" ? "#0d3b1e" : "#3b1e0d",
                      color: enochian.gate_direction === "Ascending" ? "#7CFC7C" : "#FFB27C",
                      border: `1px solid ${enochian.gate_direction === "Ascending" ? "#7CFC7C55" : "#FFB27C55"}`,
                    }}
                    title={enochian.gate_phase_label}
                  >
                    {enochian.gate_direction === "Ascending" ? "↑" : "↓"} {enochian.gate_direction}
                    {enochian.gate_status?.declination !== undefined && (
                      <span style={{ marginLeft: 6, opacity: 0.85 }}>
                        δ = {enochian.gate_status.declination.toFixed(2)}°
                      </span>
                    )}
                  </div>
                )}
                {enochian.gate_phase_label && (
                  <div
                    data-testid="watchman-gate-phase-label"
                    style={{ color: "#e8c66a", fontSize: "0.72rem", marginTop: 4, fontStyle: "italic" }}
                  >
                    {enochian.gate_phase_label.replace(/^Gate \d+ — /, "")}
                  </div>
                )}
                <div style={{ color: "#666", fontSize: "0.7rem", marginTop: "3px" }}>
                  {enochian.gate_info.description}
                </div>
              </div>

              {/* Moon Parts */}
              <div
                style={{
                  background: "#111",
                  borderRadius: "10px",
                  padding: "15px",
                  textAlign: "center",
                  border: "1px solid #87CEEB40",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "8px" }}>
                  {getMoonEmoji(moon.illumination, moon.is_waxing)}
                </div>
                <div style={{ color: "#87CEEB", fontWeight: "bold", fontSize: "1.1rem" }}>
                  {enochian.moon_parts_of_light} Parts of Light
                </div>
                <div style={{ color: "#aaa", fontSize: "0.8rem", marginTop: "5px" }}>
                  {moon.illumination.toFixed(1)}% Illuminated
                </div>
                <div style={{ color: "#666", fontSize: "0.7rem", marginTop: "3px" }}>
                  {moon.phase_name}
                </div>
              </div>
            </div>

            {/* Moon Parts Visual Scale */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ color: "#888", fontSize: "0.75rem", marginBottom: "8px" }}>
                14 PARTS OF LIGHT SCALE
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "2px",
                  height: "20px",
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                {[...Array(15)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      background:
                        i <= enochian.moon_parts_of_light
                          ? `linear-gradient(180deg, #87CEEB ${100 - (i / 14) * 100}%, #1a3a5a)`
                          : "#222",
                      transition: "all 0.3s ease",
                      position: "relative",
                    }}
                    title={`Part ${i}`}
                  >
                    {i === enochian.moon_parts_of_light && (
                      <div
                        style={{
                          position: "absolute",
                          top: "-20px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          fontSize: "0.6rem",
                          color: "#87CEEB",
                        }}
                      >
                        ▼
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "5px",
                  fontSize: "0.65rem",
                  color: "#666",
                }}
              >
                <span>New Moon</span>
                <span>First Quarter</span>
                <span>Full Moon</span>
              </div>
            </div>
            
            {/* 91-DAY QUARTERLY JUBILEE GATE PROTOCOL */}
            {biblical?.consecrated_time && (
              <div
                style={{
                  background: biblical.consecrated_time.is_jubilee_gate || biblical.consecrated_time.is_national_gate
                    ? "linear-gradient(135deg, #4a3a1a 0%, #2a2a0a 100%)"
                    : "linear-gradient(135deg, #1a2a3a 0%, #0a1a2a 100%)",
                  borderRadius: "10px",
                  padding: "12px",
                  marginBottom: "15px",
                  border: biblical.consecrated_time.is_jubilee_gate 
                    ? "2px solid #FFD700" 
                    : biblical.consecrated_time.is_national_gate
                    ? "2px solid #DDA0DD"
                    : "1px solid #4169E140",
                }}
              >
                <div style={{ 
                  color: biblical.consecrated_time.is_jubilee_gate ? "#FFD700" : "#87CEEB", 
                  fontWeight: "bold", 
                  fontSize: "0.85rem", 
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}>
                  {biblical.consecrated_time.is_jubilee_gate ? "🚪" : biblical.consecrated_time.is_national_gate ? "🏛️" : "📜"} QUARTERLY JUBILEE GATES
                </div>
                
                {/* NEW Protocol - 91-Day Reset */}
                <div style={{ 
                  background: "#0a0a0a",
                  borderRadius: "6px",
                  padding: "8px",
                  marginBottom: "8px"
                }}>
                  <div style={{ color: "#FFD700", fontSize: "0.7rem", fontWeight: "bold", marginBottom: "4px" }}>
                    91-DAY QUARTERLY RESET
                  </div>
                  <div style={{ color: "#ccc", fontSize: "0.7rem", lineHeight: "1.4" }}>
                    Every 91st day is a Stand-Alone Jubilee Gate. NOT a 13th month or 50-year cycle.
                    Days 91, 182, 273, 364 = Quarterly Gates (intercalary).
                  </div>
                </div>
                
                {/* Status Indicator */}
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  marginBottom: "6px"
                }}>
                  <span style={{ color: "#aaa", fontSize: "0.75rem" }}>Status:</span>
                  <span style={{ 
                    color: biblical.consecrated_time.is_jubilee_gate ? "#FFD700" : 
                           biblical.consecrated_time.is_national_gate ? "#DDA0DD" : "#90EE90", 
                    fontSize: "0.8rem",
                    fontWeight: "bold"
                  }}>
                    {biblical.consecrated_time.status === "QUARTERLY_JUBILEE_GATE" ? "JUBILEE GATE DAY" :
                     biblical.consecrated_time.status === "NATIONAL_JUBILEE_GATE" ? "NATIONAL GATE" : "Regular Day"}
                  </span>
                </div>
                
                {/* Quarterly Info */}
                {biblical.consecrated_time.quarterly_info && (
                  <div style={{
                    background: "#111",
                    borderRadius: "6px",
                    padding: "8px",
                    marginTop: "8px"
                  }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                      <div>
                        <div style={{ color: "#666", fontSize: "0.65rem" }}>Current Quarter</div>
                        <div style={{ color: "#87CEEB", fontSize: "0.8rem", fontWeight: "bold" }}>
                          Q{biblical.consecrated_time.quarterly_info.quarter}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#666", fontSize: "0.65rem" }}>Days to Gate</div>
                        <div style={{ color: "#FFD700", fontSize: "0.8rem", fontWeight: "bold" }}>
                          {biblical.consecrated_time.quarterly_info.days_to_next_gate}
                        </div>
                      </div>
                    </div>
                    
                    {/* Gate Status Display */}
                    <div style={{ 
                      marginTop: "8px",
                      padding: "6px",
                      background: "#0a0a0a",
                      borderRadius: "4px"
                    }}>
                      <div style={{ color: "#FFD700", fontSize: "0.65rem", marginBottom: "4px" }}>
                        {biblical.consecrated_time.quarterly_info.quarter_name}
                      </div>
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "space-between",
                        fontSize: "0.6rem",
                        color: "#888"
                      }}>
                        <span>Q1: Day 91</span>
                        <span>Q2: Day 182</span>
                        <span>Q3: Day 273</span>
                        <span>Q4: Day 364</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Visual Witness - Sun Position */}
                {biblical.consecrated_time.visual_witness && (
                  <div style={{
                    background: "#111",
                    borderRadius: "6px",
                    padding: "8px",
                    marginTop: "8px"
                  }}>
                    <div style={{ 
                      color: "#FFD700", 
                      fontSize: "0.75rem", 
                      fontWeight: "bold", 
                      marginBottom: "6px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      ☀️ VISUAL WITNESS (Sky Map)
                    </div>
                    
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                      <div>
                        <div style={{ color: "#666", fontSize: "0.65rem" }}>Sun Longitude</div>
                        <div style={{ color: "#FFD700", fontSize: "0.8rem", fontWeight: "bold" }}>
                          {biblical.consecrated_time.visual_witness.sun_longitude}°
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#666", fontSize: "0.65rem" }}>Sun Altitude</div>
                        <div style={{ color: "#FFA500", fontSize: "0.8rem", fontWeight: "bold" }}>
                          {biblical.consecrated_time.visual_witness.sun_altitude}°
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Wednesday Rule & National Gates */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                  marginTop: "8px"
                }}>
                  <div style={{ 
                    background: "#1a1a2a", 
                    padding: "6px", 
                    borderRadius: "4px",
                    textAlign: "center"
                  }}>
                    <div style={{ color: "#DDA0DD", fontSize: "0.65rem", fontWeight: "bold" }}>WEDNESDAY RULE</div>
                    <div style={{ color: "#aaa", fontSize: "0.55rem" }}>Abib 1 = Day 4</div>
                  </div>
                  <div style={{ 
                    background: "#1a2a1a", 
                    padding: "6px", 
                    borderRadius: "4px",
                    textAlign: "center"
                  }}>
                    <div style={{ color: "#90EE90", fontSize: "0.65rem", fontWeight: "bold" }}>NATIONAL GATES</div>
                    <div style={{ color: "#aaa", fontSize: "0.55rem" }}>M1, M4, M7, M10</div>
                  </div>
                </div>
                
                {/* Teaching Note */}
                <div style={{
                  marginTop: "8px",
                  padding: "6px",
                  background: "#0a0a0a",
                  borderRadius: "4px",
                  borderLeft: "2px solid #FFD700"
                }}>
                  <div style={{ color: "#FFD700", fontSize: "0.65rem", fontStyle: "italic" }}>
                    "50-Year Jubilee = Legacy Error. True Jubilee = Quarterly (91 days)."
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          background: "#0a0500",
          padding: "10px 20px",
          borderTop: "1px solid #CD853F20",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#666", fontSize: "0.7rem" }}>
          Enoch 72-74 • Jubilees 2
        </span>
        <button
          onClick={fetchCelestialData}
          style={{
            background: "transparent",
            border: "none",
            color: "#CD853F",
            cursor: "pointer",
            fontSize: "0.8rem",
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 15px rgba(0,0,0,0.3); }
          50% { transform: scale(1.05); box-shadow: 0 4px 20px rgba(255,215,0,0.5); }
        }
      `}</style>
    </div>
  );
}

export default EnochianWatchman;
export { GATE_COLORS };
